import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save } from "lucide-react";

const FIELDS = [
  { name: "legal_business_name", label: "Legal business name", required: true },
  { name: "trading_name", label: "Trading name", required: true },
  { name: "abn", label: "ABN", required: true, placeholder: "12 345 678 901" },
  { name: "acn", label: "ACN", required: false, placeholder: "123 456 789" },
  { name: "business_address", label: "Business address", required: true, full: true },
  { name: "contact_person", label: "Contact person", required: true },
  { name: "phone", label: "Phone", required: true, placeholder: "04XX XXX XXX" },
  { name: "contact_email", label: "Contact email", required: true, type: "email" },
  { name: "bank_account_name", label: "Bank account name", required: true },
  { name: "bank_bsb", label: "BSB", required: true, placeholder: "XXX-XXX" },
  { name: "bank_account_number", label: "Bank account number", required: true },
  { name: "services_offered", label: "Services offered", required: true, full: true, placeholder: "e.g. Local cartage, interstate linehaul, refrigerated freight" },
  { name: "operating_locations", label: "Operating locations", required: true, full: true, placeholder: "e.g. Sydney metro, NSW regional, Sydney–Brisbane corridor" },
  { name: "vehicle_types", label: "Vehicle types", required: true, full: true, placeholder: "e.g. Prime mover, rigid truck, tautliner, refrigerated trailer" },
];

const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.name, ""]));

export default function ProfilePage() {
  const [form, setForm] = useState(EMPTY);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile")
      .then((r) => {
        setForm({ ...EMPTY, ...r.data.profile });
        setComplete(r.data.profile_complete);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setComplete(data.profile_complete);
      toast.success(data.profile_complete ? "Profile saved — your company profile is complete" : "Profile saved. Fill all required fields to complete your profile.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div data-testid="profile-loading" className="text-sm text-muted-foreground">Loading profile…</div>;

  return (
    <div className="max-w-4xl space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Company Profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          This information is used by TAG Xpress to verify and onboard your business.
        </p>
      </div>

      <div data-testid="profile-complete-status" className={`rounded-md border px-4 py-3 text-sm font-medium ${complete ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
        {complete ? "Profile complete." : "Profile incomplete — all fields marked * are required (ACN optional)."}
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader><CardTitle className="text-base">Business details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.name} className={`space-y-1.5 ${f.full ? "md:col-span-2" : ""}`}>
                <Label htmlFor={`profile-${f.name}`}>
                  {f.label} {f.required && <span className="text-brand">*</span>}
                </Label>
                <Input
                  id={`profile-${f.name}`}
                  data-testid={`profile-${f.name}-input`}
                  type={f.type || "text"}
                  placeholder={f.placeholder || ""}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                />
              </div>
            ))}
          </CardContent>
        </Card>
        <div className="mt-6">
          <Button data-testid="save-profile-button" type="submit" disabled={saving}
            className="bg-brand font-semibold text-white transition-transform duration-150 hover:-translate-y-px hover:bg-brand-dark">
            <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}
